import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
} from '@mui/material'
import { QueryStats, Autorenew } from '@mui/icons-material'
import PropTypes from 'prop-types'
import { previewPricingRule } from '../../Utils/RestUtils'

const OUTPUT_ROWS = [
  { label: 'Guide Price (Low)', key: 'guide', color: '#f59e0b' },
  { label: 'Target Price (Medium)', key: 'target', color: '#3b82f6' },
  { label: 'Max Price (High)', key: 'max', color: '#22c55e' },
]

const formatPrice = (val) =>
  `£${Number(val).toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

const LabelText = ({ children }) => (
  <Typography
    variant="caption"
    fontWeight={600}
    color="text.secondary"
    sx={{
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      display: 'block',
      mb: 0.5,
    }}
  >
    {children}
  </Typography>
)
LabelText.propTypes = { children: PropTypes.node.isRequired }

const PriceImpactPreview = ({
  lowOperation,
  lowValue,
  mediumOperation,
  mediumValue,
  highOperation,
  highValue,
}) => {
  const [predictionValue, setPredictionValue] = useState('')
  const [capValue, setCapValue] = useState('')
  const [fee, setFee] = useState('200')
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const timerRef = useRef(null)

  useEffect(() => {
    if (predictionValue === '' || capValue === '') {
      setResult(null)
      return
    }

    if (timerRef.current) clearTimeout(timerRef.current)

    timerRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const data = await previewPricingRule({
          predictionValue: parseFloat(predictionValue),
          capValue: parseFloat(capValue),
          fee: parseFloat(fee) || 0,
          lowOperation: lowOperation || null,
          lowValue: lowValue ?? null,
          mediumOperation: mediumOperation || null,
          mediumValue: mediumValue ?? null,
          highOperation: highOperation || null,
          highValue: highValue ?? null,
        })
        setResult(data)
      } catch {
        setResult(null)
      } finally {
        setLoading(false)
      }
    }, 400)

    return () => clearTimeout(timerRef.current)
  }, [
    predictionValue,
    capValue,
    fee,
    lowOperation,
    lowValue,
    mediumOperation,
    mediumValue,
    highOperation,
    highValue,
  ])

  const hasInputs = predictionValue !== '' && capValue !== ''

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2.5,
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Box
          sx={{
            bgcolor: '#2563eb',
            borderRadius: 1.5,
            p: 0.75,
            display: 'flex',
            color: 'white',
            flexShrink: 0,
          }}
        >
          <QueryStats sx={{ fontSize: 20 }} />
        </Box>
        <Box>
          <Typography variant="subtitle1" fontWeight={600} lineHeight={1.3}>
            Price Impact Preview
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Visualise rule effect in real time
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
        <Box>
          <LabelText>Predicted Value (£)</LabelText>
          <TextField
            size="small"
            fullWidth
            placeholder="e.g. 5000"
            value={predictionValue}
            onChange={(e) => setPredictionValue(e.target.value)}
            type="number"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">£</InputAdornment>
              ),
            }}
          />
        </Box>
        <Box>
          <LabelText>Cap Value (£)</LabelText>
          <TextField
            size="small"
            fullWidth
            placeholder="e.g. 4500"
            value={capValue}
            onChange={(e) => setCapValue(e.target.value)}
            type="number"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">£</InputAdornment>
              ),
            }}
          />
        </Box>
        <Box>
          <LabelText>Fee (£)</LabelText>
          <TextField
            size="small"
            fullWidth
            value={fee}
            onChange={(e) => setFee(e.target.value)}
            type="number"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">£</InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      <LabelText>Adjusted Prices</LabelText>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2 }}>
        {OUTPUT_ROWS.map(({ label, key, color }) => (
          <Box
            key={key}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: color,
                flexShrink: 0,
              }}
            />
            <Box>
              <Typography
                variant="caption"
                fontWeight={600}
                display="block"
                lineHeight={1.3}
                sx={{ textTransform: 'uppercase', fontSize: '0.65rem' }}
              >
                {label}
              </Typography>
              {loading ? (
                <CircularProgress size={12} sx={{ mt: 0.25 }} />
              ) : (
                <Typography
                  variant="caption"
                  color={result ? 'text.primary' : 'text.disabled'}
                >
                  {result
                    ? formatPrice(result[key])
                    : 'Enter a predicted value'}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {!hasInputs && (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            p: 2,
            textAlign: 'center',
            bgcolor: 'action.hover',
          }}
        >
          <Autorenew sx={{ color: 'text.disabled', fontSize: 22, mb: 0.5 }} />
          <Typography variant="caption" color="text.secondary" display="block">
            Enter a predicted value above to see how the current band
            adjustments would affect the output prices.
          </Typography>
        </Box>
      )}
    </Box>
  )
}

PriceImpactPreview.propTypes = {
  lowOperation: PropTypes.string,
  lowValue: PropTypes.number,
  mediumOperation: PropTypes.string,
  mediumValue: PropTypes.number,
  highOperation: PropTypes.string,
  highValue: PropTypes.number,
}

PriceImpactPreview.defaultProps = {
  lowOperation: null,
  lowValue: null,
  mediumOperation: null,
  mediumValue: null,
  highOperation: null,
  highValue: null,
}

export default PriceImpactPreview
